import type { Card, Finish } from './collection.ts';

// Which kind of foil a copy is. Scryfall lists a special foil among the
// printing's promo types ("surgefoil", "galaxyfoil"…); it applies to the
// printing's foil finish, so a Final Fantasy precon card that comes nonfoil
// and surge foil is a surge foil only when the copy is foil.

/** Special foils by Scryfall promo type, in the order the foil-type filter lists them. */
export const SPECIAL_FOILS = {
  chocobotrackfoil: 'Chocobo Track foil',
  confettifoil: 'Confetti foil',
  doublerainbow: 'Double rainbow foil',
  dragonscalefoil: 'Dragonscale foil',
  firstplacefoil: 'First-place foil',
  fracturefoil: 'Fracture foil',
  galaxyfoil: 'Galaxy foil',
  gilded: 'Gilded foil',
  halofoil: 'Halo foil',
  manafoil: 'Mana foil',
  neonink: 'Neon ink foil',
  oilslick: 'Oil slick foil',
  rainbowfoil: 'Rainbow foil',
  raisedfoil: 'Raised foil',
  ripplefoil: 'Ripple foil',
  silverfoil: 'Silver foil',
  /** Silver scroll foils (SOA); The Lord of the Rings' scroll showcase foils are "silverfoil" instead. */
  silverscroll: 'Silver scroll foil',
  stepandcompleat: 'Step-and-compleat foil',
  surgefoil: 'Surge foil',
  textured: 'Textured foil',
} as const satisfies Record<string, string>;

/** A copy's foil type: "foil" for a plain foil, "etched", or a special foil's promo type. */
export type FoilType = { id: string; label: string };

/** The foil finish's types, for a filter: plain foil, then the special foils. (Etched is a finish of its own.) */
export const FOIL_TYPES: FoilType[] = [
  { id: 'foil', label: 'Plain foil' },
  ...Object.entries(SPECIAL_FOILS).map(([id, label]) => ({ id, label })),
];

/**
 * The copy's foil type, or null for non-foil. Promo types Scryfall adds later
 * that end in "foil" still get a label ("cosmicfoil" → "Cosmic foil").
 */
export function foilType(card: Pick<Card, 'promoTypes'>, finish: Finish): FoilType | null {
  if (finish === 'nonfoil') return null;
  if (finish === 'etched') return { id: 'etched', label: 'Etched' };
  for (const p of card.promoTypes) {
    if (p in SPECIAL_FOILS) return { id: p, label: SPECIAL_FOILS[p as keyof typeof SPECIAL_FOILS] };
  }
  const other = card.promoTypes.find((p) => /^[a-z]+foil$/.test(p));
  if (other) return { id: other, label: `${other[0]!.toUpperCase()}${other.slice(1, -4)} foil` };
  return { id: 'foil', label: 'Foil' };
}
